import { renderCell, renderHeader } from '../components/table';
import { useEffect, useMemo, useContext } from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Wrapper } from '../components/Wrapper';
import { useDispatch, useSelector } from 'react-redux';
import { fetchEeoVisual } from '@/store/atsReports/eeo/actions';
import { Center, Spinner } from '@chakra-ui/react';
import { EmptyDataWrapper } from '../components/empty-data-wrapper';
import { TitleWithTooltip } from '../components/title-with-tooltip';
import { ReportSelectionContext } from '@/contextProviders/ReportSelection';
import { Table } from '@/components/ats';
import { KText } from '@/ui-library/components';
import { HeaderCell } from '@/components/ats/Table/tableCells/HeaderCell';

export const RaceTable = ({
  isReadOnly = false,
  customData = undefined,
  renderTitle = undefined,
  isStaticReport = false,
  isEditMode = false,
  tableColStyles = {},
  onTableColDataChange = undefined,
  initialTableColData = undefined,
  skipLocalStorage = false,
}) => {
  const dispatch = useDispatch();
  const { consumer } = useContext(ReportSelectionContext);
  const filters = useSelector((state: any) => state?.eeoReports?.filters);
  const timeFrame = useSelector((state: any) => state?.eeoReports?.time);

  const { data, isLoading } = useSelector(
    (state: any) => state?.eeoReports?.race
  );

  const dataToUse = customData ? customData : data;

  useEffect(() => {
    if (!customData) {
      dispatch(
        fetchEeoVisual({
          timeFrame,
          filters,
          group: {
            group_by_1: {
              id: 'race',
            },
            group_by_2: {
              id: 'gender',
            },
          },
        })
      );
    }
  }, [timeFrame, dispatch, filters, customData]);

  const columns = useMemo(() => {
    return [
      {
        ...renderHeader({
          accessor: 'header',
          title: 'Race',
          width: isReadOnly ? 150 : 250,
          isReadOnly,
          sort: true,
          renderFooter: () => {
            return <KText size="sm-semi-bold">Total by gender</KText>;
          },
        }),
        id: 'header',
        title: 'Race',
        columnConfig: false, // This column should not be configurable
      },

      {
        Header: <HeaderCell title="Male" minW="100%" textAlign="center" />,
        title: 'Male',
        id: 'male_group',
        columnConfig: true,
        columns: [
          renderCell({
            title: 'A',
            accessor: 'male.applied',
            render: (_, row) => {
              return row?.['Male']?.applied ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Male']?.applied;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'I',
            accessor: 'male.interviewed',
            render: (_, row) => {
              return row?.['Male']?.interviewed ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Male']
                ?.interviewed;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'O',
            accessor: 'male.offered',
            render: (_, row) => {
              return row?.['Male']?.offered ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Male']?.offered;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'H',
            accessor: 'male.hired',
            render: (_, row) => {
              return row?.['Male']?.hired ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Male']?.hired;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'R',
            accessor: 'male.rejected',
            render: (_, row) => {
              return row?.['Male']?.rejected ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Male']?.rejected;
            },
            hidePercent: true,
            columnConfig: true,
          }),
        ],
      },

      {
        Header: <HeaderCell title="Female" minW="100%" textAlign="center" />,
        title: 'Female',
        id: 'female_group',
        columnConfig: true,
        columns: [
          renderCell({
            title: 'A',
            accessor: 'female.applied',
            render: (_, row) => {
              return row?.['Female']?.applied ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Female']
                ?.applied;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'I',
            accessor: 'female.interviewed',
            render: (_, row) => {
              return row?.['Female']?.interviewed ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Female']
                ?.interviewed;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'O',
            accessor: 'female.offered',
            render: (_, row) => {
              return row?.['Female']?.offered ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Female']
                ?.offered;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'H',
            accessor: 'female.hired',
            render: (_, row) => {
              return row?.['Female']?.hired ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Female']?.hired;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'R',
            accessor: 'female.rejected',
            render: (_, row) => {
              return row?.['Female']?.rejected ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Female']
                ?.rejected;
            },
            hidePercent: true,
            columnConfig: true,
          }),
        ],
      },

      {
        Header: (
          <HeaderCell title="Not-identified" minW="100%" textAlign="center" />
        ),
        title: 'Not-identified',
        id: 'not_identified_group',
        columnConfig: true,
        columns: [
          renderCell({
            title: 'A',
            accessor: 'not_identified.applied',
            render: (_, row) => {
              return row?.['Not Identified']?.applied ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Not Identified']
                ?.applied;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'I',
            accessor: 'not_identified.interviewed',
            render: (_, row) => {
              return row?.['Not Identified']?.interviewed ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Not Identified']
                ?.interviewed;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'O',
            accessor: 'not_identified.offered',
            render: (_, row) => {
              return row?.['Not Identified']?.offered ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Not Identified']
                ?.offered;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'H',
            accessor: 'not_identified.hired',
            render: (_, row) => {
              return row?.['Not Identified']?.hired ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Not Identified']
                ?.hired;
            },
            hidePercent: true,
            columnConfig: true,
          }),
          renderCell({
            title: 'R',
            accessor: 'not_identified.rejected',
            render: (_, row) => {
              return row?.['Not Identified']?.rejected ?? 0;
            },
            width: isReadOnly ? 50 : 80,
            sort: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.columnTotal?.['Not Identified']
                ?.rejected;
            },
            hidePercent: true,
            columnConfig: true,
          }),
        ],
      },

      {
        Header: <HeaderCell title="Totals" minW="100%" textAlign="center" />,
        title: 'Totals',
        id: 'totals_group',
        columnConfig: true,
        columns: [
          renderCell({
            title: 'T.A',
            accessor: 'applied',
            render: (_, row) => {
              return row?.applied ?? 0;
            },
            width: isReadOnly ? 80 : 100,
            sort: true,
            hidePercent: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.rowTotal?.applied;
            },
            columnConfig: true,
          }),
          renderCell({
            title: 'T.H',
            accessor: 'hired',
            render: (_, row) => {
              return row?.hired ?? 0;
            },
            width: isReadOnly ? 80 : 100,
            sort: true,
            hidePercent: true,
            renderFooter: (info) => {
              return info?.rows?.[0]?.original?.rowTotal?.hired;
            },
            columnConfig: true,
          }),
        ],
      },
    ];
  }, []); //eslint-disable-line

  return (
    <Wrapper
      mt={!customData && '18px'}
      isReadOnly={isReadOnly || isStaticReport}
      role="group"
    >
      {consumer === 'DEFAULT' &&
        (renderTitle ? (
          renderTitle?.()
        ) : (
          <Box flex="1">
            <TitleWithTooltip
              label="Race"
              metricDefinition="Breakdown of the races"
            />
          </Box>
        ))}

      {isLoading ? (
        <Center minHeight="200px">
          <Spinner color="primary-800" size="sm" />
        </Center>
      ) : (
        <EmptyDataWrapper data={dataToUse}>
          <Box mt="16px" h={'100%'}>
            <Table
              tableId="race-table"
              isColumnLevelSorting
              defaultSortBy={[{ id: 'header', desc: false }]}
              enableColumnConfig={isEditMode}
              columns={columns}
              data={dataToUse}
              enableFooter={true}
              layoutScrollContainerStyles="c-data-table wide-content table-container"
              enableExpanded={true}
              layoutStyles={{
                borderBottom: 'none',
              }}
              tableColStyles={tableColStyles}
              onTableColDataChange={onTableColDataChange}
              initialTableColData={initialTableColData}
              skipLocalStorage={skipLocalStorage}
            />
            <Flex>
              <Box
                flex="1"
                fontWeight="semiBold"
                fontSize={'12px'}
                color="secondary-700"
                textTransform="uppercase"
                mt="20px"
              >
                A: APPLIED, I: interviewed, O: offered, H: hired, R: rejected
              </Box>
              <Box
                fontWeight="semiBold"
                fontSize={'12px'}
                color="secondary-700"
                textTransform="uppercase"
                mt="20px"
              >
                T.A: TOTAL APPLIED, T.H: TOTAL HIRED
              </Box>
            </Flex>
          </Box>
        </EmptyDataWrapper>
      )}
    </Wrapper>
  );
};
